import React from 'react'

import { bemJoin } from 'bem-join'
import { Stack } from 'component-library-next'

import { MessageType } from '@app/graphql/types'
import { FormattedDate, useIntl } from '@app/intl/Intl'
import { FormattedMetadata } from '@app/modules/PrintView/Components/FormattedMetadata/FormattedMetadata'
import { FormattedQuestionnaire } from '@app/modules/PrintView/Components/FormattedQuestionnaire/FormattedQuestionnaire'
import { LabelledValue } from '@app/modules/PrintView/Components/LabelledValue/LabelledValue'

import './MessagesList.scss'

type MessageForList = {
  lawyer: {
    firstName?: string
    lastName?: string
  }
  type: MessageType
  topic: string
  topicQuestionnaireAnswers: Record<string, string>
  content: string
  documents: {
    id: number
    name: string
    category?: string
  }[]
  createdAt: Date
  sentAt: Date
  approvedAt: Date
  appointmentAt?: Date
  id: number
  customer: {
    firstName?: string
    lastName?: string
  }
  utilityBillMetadata?: {
    moveInDate?: string | null
    moveOutDate?: string | null
    sizeOfFlat?: number | null
    isCurrentAddress?: boolean | null
  }
}

type Props = {
  messages: MessageForList[]
}

const b = bemJoin('messages-list')

export const MessagesList = ({ messages }: Props) => {
  const { formatMessage } = useIntl()

  return (
    <div className={b('container')}>
      <h2 className={b('title')}>{formatMessage({ id: 'PrintView.MessagesList.Title' })}</h2>
      {messages.map((message) => {
        return (
          <div key={message.id} className={b('message--container')}>
            <Stack>
              {message.type === MessageType.Question && (
                <LabelledValue
                  label={formatMessage({ id: 'PrintView.MessagesList.Topic' })}
                  value={message.topic}
                />
              )}
              <div className={b('message--dates')}>
                <LabelledValue
                  label={formatMessage({ id: 'PrintView.MessagesList.SentAt' })}
                  value={
                    message.type === MessageType.Question ? (
                      <FormattedDate value={message.sentAt} />
                    ) : (
                      <FormattedDate value={message.createdAt} />
                    )
                  }
                />
                {message.type === MessageType.Question && (
                  <LabelledValue
                    label={formatMessage({ id: 'PrintView.MessagesList.ApprovedAt' })}
                    value={<FormattedDate value={message.approvedAt} />}
                  />
                )}
                {message.appointmentAt && (
                  <LabelledValue
                    label={formatMessage({ id: 'PrintView.MessagesList.AppointmentAt' })}
                    value={<FormattedDate value={message.appointmentAt} />}
                  />
                )}
              </div>
              <LabelledValue
                label={formatMessage({ id: 'PrintView.MessagesList.From' })}
                value={
                  message.type === MessageType.Question ? (
                    <>
                      {message.customer.firstName} {message.customer.lastName}
                    </>
                  ) : (
                    <>
                      {message.lawyer?.firstName} {message.lawyer?.lastName}
                    </>
                  )
                }
              />
              {message.topicQuestionnaireAnswers && (
                <FormattedQuestionnaire questionnaire={message.topicQuestionnaireAnswers} />
              )}
              <LabelledValue
                label={formatMessage({ id: 'PrintView.MessagesList.Content' })}
                value={<div>{message.content}</div>}
              />
              {!!message.documents?.length && (
                <LabelledValue
                  label={formatMessage({ id: 'PrintView.MessagesList.Documents' })}
                  value={
                    <ul>
                      {message.documents?.map((doc) => {
                        return (
                          <li key={doc.id}>
                            {doc.name}{' '}
                            {'category' in doc && !!doc.category ? `(${doc.category})` : ''}
                          </li>
                        )
                      })}
                    </ul>
                  }
                />
              )}
              {message.utilityBillMetadata && (
                <LabelledValue
                  label={formatMessage({ id: 'PrintView.MessagesList.UtilityBillInfo' })}
                  value={<FormattedMetadata metadata={message.utilityBillMetadata} />}
                />
              )}
            </Stack>
          </div>
        )
      })}
    </div>
  )
}
